import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DIGEST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Bell,
  BellOff,
  Mail,
  Sparkles,
  Loader2,
} from "lucide-react";

const SHARE_TOKENS_KEY = "imali:share-tokens";

const TIMEZONES = [
  "UTC",
  "Africa/Nairobi", "Africa/Lagos", "Africa/Johannesburg", "Africa/Cairo",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Warsaw",
  "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Toronto",
  "America/Sao_Paulo",
  "Asia/Kolkata", "Asia/Tokyo", "Asia/Shanghai", "Asia/Singapore",
  "Asia/Dubai", "Asia/Riyadh",
  "Australia/Sydney", "Pacific/Auckland",
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function loadShareTokens() {
  try { return JSON.parse(localStorage.getItem(SHARE_TOKENS_KEY) || "{}"); }
  catch { return {}; }
}

export default function DigestSettings() {
  const tokens = useMemo(() => loadShareTokens(), []);
  const shareIds = useMemo(() => Object.keys(tokens), [tokens]);

  const [shareId, setShareId] = useState(shareIds[0] || "");
  const [email, setEmail] = useState("");
  const [tz, setTz] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  );
  const [hour, setHour] = useState(8);

  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");

  const token = shareId ? tokens[shareId] : null;

  useEffect(() => {
    if (!shareId || !token) {
      setExisting(null);
      setHistory([]);
      return;
    }
    setLoading(true);
    Promise.allSettled([
      api.get(`/digests/subscription?share_id=${shareId}`, {
        headers: { "X-Owner-Token": token },
      }),
      api.get(`/digests?share_id=${shareId}`, {
        headers: { "X-Owner-Token": token },
      }),
    ]).then(([subRes, listRes]) => {
      if (subRes.status === "fulfilled" && subRes.value.data) {
        const s = subRes.value.data;
        setExisting(s);
        setEmail(s.email);
        setTz(s.timezone);
        setHour(s.hour_local);
      } else {
        setExisting(null);
      }
      if (listRes.status === "fulfilled") setHistory(listRes.value.data);
    }).finally(() => setLoading(false));
  }, [shareId, token]);

  const save = async () => {
    if (!shareId || !token) {
      toast.error("Create a share first from /compare");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        "/digests/subscribe",
        { share_id: shareId, email, timezone: tz, hour_local: Number(hour) },
        { headers: { "X-Owner-Token": token } }
      );
      setExisting(data);
      toast.success("Subscribed — your morning digest is scheduled.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to save");
    } finally {
      setSaving(false);
    }
  };

  const unsubscribe = async () => {
    if (!shareId || !token) return;
    if (!window.confirm("Stop daily digests for this share?")) return;
    try {
      await api.delete(`/digests/subscription?share_id=${shareId}`, {
        headers: { "X-Owner-Token": token },
      });
      setExisting(null);
      toast.success("Unsubscribed");
    } catch {
      toast.error("Unable to unsubscribe");
    }
  };

  const preview = async () => {
    if (!existing) {
      toast.error("Save your settings first, then preview.");
      return;
    }
    try {
      const { data } = await api.post(
        `/digests/preview?share_id=${shareId}`,
        null,
        { headers: { "X-Owner-Token": token } }
      );
      setPreviewHtml(data.html);
      setHistory((prev) => [data, ...prev].slice(0, 20));
      toast.success(`Preview generated (${data.status})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nothing due within 24 h to preview");
    }
  };

  if (shareIds.length === 0) return null;

  return (
    <section
      data-testid={DIGEST.panel}
      className="rounded-2xl border border-primary/25 bg-primary/[.04] p-6 lg:p-8"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bell aria-hidden="true" className="w-4 h-4 text-primary" />
          <h2 className="font-display text-2xl tracking-tight">Daily digest</h2>
          <span className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold ml-2">
            Mocked delivery
          </span>
        </div>
        {existing && (
          <Button
            data-testid={DIGEST.unsubscribe}
            variant="ghost"
            size="sm"
            onClick={unsubscribe}
            className="rounded-full text-muted-foreground hover:text-destructive"
          >
            <BellOff aria-hidden="true" className="w-3.5 h-3.5 mr-1" />
            Unsubscribe
          </Button>
        )}
      </div>

      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        One email per day summarising follow-ups due within 24 h. Currently
        queued to a mocked outbox — set{" "}
        <code className="font-mono text-xs">RESEND_API_KEY</code> in{" "}
        <code className="font-mono text-xs">backend/.env</code> and verify your
        domain on Resend to go live.{" "}
        <a
          href={`/email-events?share_id=${shareId}`}
          className="text-primary hover:underline font-medium"
        >
          View delivery events →
        </a>
      </p>

      {shareIds.length > 1 && (
        <div className="mt-5">
          <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Comparison
          </Label>
          <Select value={shareId} onValueChange={setShareId}>
            <SelectTrigger data-testid={DIGEST.shareSelect} className="mt-1.5 max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {shareIds.map((id) => (
                <SelectItem key={id} value={id}>
                  {id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Recipient email
          </Label>
          <Input
            data-testid={DIGEST.email}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Timezone
          </Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid={DIGEST.timezone} className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {TIMEZONES.map((z) => (
                <SelectItem key={z} value={z}>{z}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Send at (local)
          </Label>
          <Select value={String(hour)} onValueChange={(v) => setHour(Number(v))}>
            <SelectTrigger data-testid={DIGEST.hour} className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {String(h).padStart(2, "0")}:00
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          data-testid={DIGEST.save}
          onClick={save}
          disabled={saving || loading}
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {saving ? (
            <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Mail aria-hidden="true" className="w-4 h-4 mr-2" />
          )}
          {existing ? "Update settings" : "Subscribe"}
        </Button>
        <Button
          data-testid={DIGEST.preview}
          variant="outline"
          onClick={preview}
          disabled={!existing}
          className="rounded-full"
        >
          <Sparkles aria-hidden="true" className="w-4 h-4 mr-2" />
          Preview digest now
        </Button>
      </div>

      {history.length > 0 && (
        <div className="mt-6 border-t border-border pt-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Recent digests (mock outbox)
          </p>
          <ul
            data-testid={DIGEST.history}
            className="mt-3 space-y-2 text-xs font-mono text-muted-foreground"
          >
            {history.slice(0, 5).map((d) => (
              <li key={d.id} className="flex items-baseline gap-3">
                <span className="w-40 shrink-0">
                  {new Date(d.delivered_at).toLocaleString()}
                </span>
                <span className="text-foreground/85 flex-1 truncate">{d.subject}</span>
                <span className="text-secondary">{d.status}</span>
                <button
                  type="button"
                  onClick={() => setPreviewHtml(d.html)}
                  className="text-primary hover:underline"
                >
                  view
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={!!previewHtml} onOpenChange={(v) => !v && setPreviewHtml("")}>
        <DialogContent
          data-testid={DIGEST.previewDialog}
          className="max-w-3xl bg-background"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              Digest preview
            </DialogTitle>
            <DialogDescription>
              This is the exact HTML that would be delivered when your daily
              digest fires.
            </DialogDescription>
          </DialogHeader>
          <iframe
            data-testid={DIGEST.previewIframe}
            srcDoc={previewHtml}
            title="Digest preview"
            className="w-full h-[70vh] rounded-lg border border-border bg-white"
            sandbox=""
          />
        </DialogContent>
      </Dialog>
    </section>
  );
}
